import { isValidPendingEventRegistration, PendingEventRegistration } from './eventRegistrationIntent'

const DATABASE = 'essentials-event-registration'
const STORE = 'intent'
const KEY = 'pending'
const CONSUMED_KEY = 'lastConsumedId'

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'))
      return
    }
    const request = indexedDB.open(DATABASE, 1)
    let settled = false
    const fail = (error: unknown) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      reject(error)
    }
    // A blocked browser database must not indefinitely hold authentication/navigation.
    const timeout = setTimeout(() => fail(new Error('IndexedDB open timed out')), 3000)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE)
    request.onerror = () => fail(request.error)
    request.onblocked = () => fail(new Error('IndexedDB blocked'))
    request.onsuccess = () => {
      if (settled) {
        request.result.close()
        return
      }
      settled = true
      clearTimeout(timeout)
      resolve(request.result)
    }
  })

// All inspection/consumption happens in a single read-write transaction. Resolve only
// after commit: another tab cannot read the intent that this transaction consumed.
const transact = async <T>(
  fallback: T,
  operation: (store: IDBObjectStore, finish: (value: T) => void) => void,
): Promise<T> => {
  let database: IDBDatabase | undefined
  try {
    database = await openDatabase()
    return await new Promise<T>((resolve, reject) => {
      const transaction = database!.transaction(STORE, 'readwrite')
      let result = fallback
      transaction.oncomplete = () => resolve(result)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
      operation(transaction.objectStore(STORE), (value) => {
        result = value
      })
    })
  } catch {
    return fallback
  } finally {
    database?.close()
  }
}

export const saveEventRegistrationIntent = async (intent: PendingEventRegistration): Promise<boolean> => {
  if (!isValidPendingEventRegistration(intent)) return false
  let supersededId: string | undefined
  const saved = await transact(false, (store, finish) => {
    const consumed = store.get(CONSUMED_KEY)
    consumed.onsuccess = () => {
      if (typeof consumed.result === 'string') supersededId = consumed.result
    }
    const request = store.get(KEY)
    request.onsuccess = () => {
      // Invalidate the replaced choice, including an unsent consumed choice, even if
      // this write fails. Cleanup must not remove another tab's newer choice.
      if (isValidPendingEventRegistration(request.result)) supersededId = request.result.intentId
      store.put(intent, KEY)
      store.delete(CONSUMED_KEY)
      finish(true)
    }
  })
  if (!saved && supersededId) await clearEventRegistrationIntent(supersededId)
  return saved
}

const accessIntent = (
  action: 'read' | 'consume' | 'clear',
  expectedId?: string,
): Promise<PendingEventRegistration | null> =>
  transact<PendingEventRegistration | null>(null, (store, finish) => {
    if (action === 'clear') {
      const consumed = store.get(CONSUMED_KEY)
      consumed.onsuccess = () => {
        if (expectedId === undefined || consumed.result === expectedId) store.delete(CONSUMED_KEY)
      }
    }
    const request = store.get(KEY)
    request.onsuccess = () => {
      const intent: unknown = request.result
      if (!isValidPendingEventRegistration(intent)) {
        store.delete(KEY)
        return
      }
      if (expectedId !== undefined && intent.intentId !== expectedId) return
      if (action !== 'read') store.delete(KEY)
      if (action === 'consume') store.put(intent.intentId, CONSUMED_KEY)
      finish(intent)
    }
  })

export const readEventRegistrationIntent = () => accessIntent('read')
export const consumeEventRegistrationIntent = (expectedId: string) => accessIntent('consume', expectedId)
export const clearEventRegistrationIntent = (expectedId?: string) => accessIntent('clear', expectedId)

/** Only for a request that was never sent. A later save, consume or clear prevents restoration. */
export const restoreUndispatchedEventRegistrationIntent = (
  intent: PendingEventRegistration,
  canRestore: () => boolean,
): Promise<boolean> =>
  transact(false, (store, finish) => {
    const consumed = store.get(CONSUMED_KEY)
    consumed.onsuccess = () => {
      if (consumed.result !== intent.intentId) return
      const pending = store.get(KEY)
      pending.onsuccess = () => {
        if (pending.result !== undefined || !isValidPendingEventRegistration(intent) || !canRestore()) return
        store.put(intent, KEY)
        store.delete(CONSUMED_KEY)
        finish(true)
      }
    }
  })
